import { useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  productService,
  warehouseService,
  categoryService,
  employeeService,
  userApiService,
  salesOrderService,
  purchaseOrderService,
  roleService,
  menuService,
  subscriptionPlanService,
  tenantService,
  customerService,
  supplierService,
  currencyService,
  brandService,
  unitService,
} from '@kss/api';

/**
 * Source services we know how to resolve. Add more as needed — every
 * key here becomes a valid `kind` to pass to `useNameMap`.
 */
const SOURCES = {
  product: { service: productService, idKey: 'productId', nameKey: 'name' },
  warehouse: { service: warehouseService, idKey: 'warehouseId', nameKey: 'name' },
  category: { service: categoryService, idKey: 'categoryId', nameKey: 'name' },
  employee: {
    service: employeeService,
    idKey: 'employeeId',
    nameKey: '__employeeFullName',
  },
  user: { service: userApiService, idKey: 'userId', nameKey: 'userName' },
  salesOrder: { service: salesOrderService, idKey: 'salesOrderId', nameKey: 'orderNumber' },
  purchaseOrder: { service: purchaseOrderService, idKey: 'purchaseOrderId', nameKey: 'poNumber' },
  role: { service: roleService, idKey: 'roleId', nameKey: 'name' },
  menu: { service: menuService, idKey: 'menuId', nameKey: 'name' },
  subscriptionPlan: { service: subscriptionPlanService, idKey: 'subscriptionPlanId', nameKey: 'name' },
  tenant: { service: tenantService, idKey: 'tenantId', nameKey: 'name' },
  customer: { service: customerService, idKey: 'customerId', nameKey: 'name' },
  supplier: { service: supplierService, idKey: 'supplierId', nameKey: 'name' },
  currency: { service: currencyService, idKey: 'currencyId', nameKey: 'code' },
  brand: { service: brandService, idKey: 'brandId', nameKey: 'name' },
  unit: { service: unitService, idKey: 'unitId', nameKey: 'name' },
} as const;

export type NameMapKind = keyof typeof SOURCES;

function readDisplayName(row: Record<string, unknown>, nameKey: string): string {
  if (nameKey === '__employeeFullName') {
    const first = String(row.firstName ?? '').trim();
    const last = String(row.lastName ?? '').trim();
    const full = [first, last].filter(Boolean).join(' ');
    return full || String(row.employeeCode ?? '');
  }
  const v = row[nameKey];
  return v == null ? '' : String(v);
}

export interface NameMap {
  /** id → display name. Misses fall back to the empty string. */
  byId: Record<string, string>;
  /**
   * id → display name with a clean fallback. When the id is not in the
   * map (still loading, deleted entity, etc.) returns the truncated id
   * rather than an empty cell.
   */
  resolve: (id: string | null | undefined) => string;
  loading: boolean;
}

/**
 * Loads one entity's `all()` once and exposes id→name lookups. Designed
 * to back DataGrid columns that today render raw UUIDs (`productId`,
 * `storeId`, `warehouseId`, etc.).
 *
 * Usage:
 *   const products = useNameMap('product');
 *   render: (r) => products.resolve(r.productId)
 */
// Module-level cache so N components calling `useNameMap('product')`
// share a single fetch + a single map. Without this, every `<NameCell>`
// in a 100-row grid would each fire its own `productService.all()`.
const _cache: Partial<Record<NameMapKind, Promise<Record<string, string>>>> = {};
const _resolved: Partial<Record<NameMapKind, Record<string, string>>> = {};

function fetchMap(kind: NameMapKind): Promise<Record<string, string>> {
  const existing = _cache[kind];
  if (existing) return existing;
  const cfg = SOURCES[kind];
  const p = (async () => {
    try {
      const data = await cfg.service.all();
      const next: Record<string, string> = {};
      for (const row of (Array.isArray(data) ? data : []) as Record<string, unknown>[]) {
        const id = row[cfg.idKey];
        if (typeof id !== 'string' || !id) continue;
        next[id] = readDisplayName(row, cfg.nameKey);
      }
      _resolved[kind] = next;
      return next;
    } catch {
      _resolved[kind] = {};
      return {};
    }
  })();
  _cache[kind] = p;
  return p;
}

/** Drop the cached promise for a kind so the next mount re-fetches. */
export function invalidateNameMap(kind: NameMapKind): void {
  delete _cache[kind];
  delete _resolved[kind];
}

export function useNameMap(kind: NameMapKind): NameMap {
  const cached = _resolved[kind];
  const [byId, setById] = useState<Record<string, string>>(cached ?? {});
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    setLoading(true);
    fetchMap(kind).then((next) => {
      if (!cancelled) {
        setById(next);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [kind, cached]);

  return useMemo<NameMap>(
    () => ({
      byId,
      loading,
      resolve: (id) => {
        if (!id) return '—';
        const hit = byId[String(id)];
        if (hit) return hit;
        // Truncated id is a useful fallback while loading (or when the
        // referenced entity has been deleted).
        return loading ? '…' : `${String(id).slice(0, 8)}…`;
      },
    }),
    [byId, loading],
  );
}

/**
 * Drop-in cell renderer for DataGrid columns that today render raw
 * UUIDs. Uses the cached `useNameMap` so a 100-row grid still fires
 * exactly one fetch per `kind`.
 *
 *   { accessor: 'productId',
 *     render: (r) => <NameCell kind="product" id={r.productId} /> }
 */
export function NameCell({
  kind,
  id,
}: {
  kind: NameMapKind;
  id: string | null | undefined;
}): ReactElement {
  const map = useNameMap(kind);
  return <span className="text-sm">{map.resolve(id)}</span>;
}

/**
 * Convenience wrapper — load several name maps in one call. The hook
 * runs each `useNameMap` independently so they fetch in parallel.
 *
 *   const { product, store, warehouse } = useNameMaps('product','store','warehouse');
 */
export function useNameMaps<K extends NameMapKind>(...kinds: K[]): Record<K, NameMap> {
  // We deliberately don't loop with Array.from — React rules-of-hooks
  // forbid hooks in loops. Up to 6 maps is enough for every page in the
  // repo today; expand if a list page needs more.
  const a = useNameMap(kinds[0] ?? ('product' as K));
  const b = useNameMap(kinds[1] ?? ('product' as K));
  const c = useNameMap(kinds[2] ?? ('product' as K));
  const d = useNameMap(kinds[3] ?? ('product' as K));
  const e = useNameMap(kinds[4] ?? ('product' as K));
  const f = useNameMap(kinds[5] ?? ('product' as K));
  const all = [a, b, c, d, e, f];
  const out = {} as Record<K, NameMap>;
  kinds.forEach((k, i) => {
    out[k] = all[i]!;
  });
  return out;
}
